import React, { createContext, PropsWithChildren, useContext, useState } from 'react';

interface ISelectedItemsContext<T>{
  selectedItems: T[];
  addItem: (item: T) => void;
  removeItem: (item: T) => void;
  updateItems: (items: T[]) => void;
  clearItems: () => void;
}

export function createSelectedItemsContext<T>() {
    return createContext<ISelectedItemsContext<T>>({} as ISelectedItemsContext<T>);
}

interface IProvideSelectedItemsProps<T> {
    selectedItemsContext: React.Context<ISelectedItemsContext<T>>;
};

export function ProvideSelectedItems<T extends { id: string }>({
    children,
    selectedItemsContext,
}: PropsWithChildren<IProvideSelectedItemsProps<T>>) {
  const [selectedItems, setSelectedItems] = useState<T[]>([]);

  const addItem = (item: T) => {
    setSelectedItems([...selectedItems, item]);
  };
  
  const removeItem = (item: T) => {
    setSelectedItems(selectedItems.filter(selectedItem => selectedItem.id !== item.id));
  };

  const updateItems = (items: T[]) => {
    setSelectedItems([...items]);
  };
  
  const clearItems = () => {
    setSelectedItems([]);
  };

  return (
      <selectedItemsContext.Provider
          value={{
            selectedItems,
            addItem,
            removeItem,
            updateItems,
            clearItems
          }}
      >
          {children}
      </selectedItemsContext.Provider>
  );
}

export function useSelectedItems<T>(
    selectedItemsContext: React.Context<ISelectedItemsContext<T>>,
) {
    const context = useContext(selectedItemsContext);

    if(!context) {
      throw new Error('selectedItemContext not found')
    }

    const { selectedItems, addItem, removeItem, updateItems, clearItems } = context;

    return {
      selectedItems,
      addItem,
      removeItem,
      updateItems,
      clearItems
    };
}